import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Search, Download, Eye, ArrowLeft, Calendar, User, TrendingUp, Loader2, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

function AnimatedNumber({ value, duration = 1 }) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  useEffect(() => {
    if (isInView && value > 0) {
      let start = 0;
      const end = parseInt(value);
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) { setDisplayValue(end); clearInterval(timer); }
        else setDisplayValue(Math.floor(start));
      }, 1000 / 60);
      return () => clearInterval(timer);
    } else if (value === 0) setDisplayValue(0);
  }, [isInView, value, duration]);
  return <span ref={ref}>{displayValue}</span>;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS_PER_PAGE = 10;

export default function MyPlans({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientFilter = searchParams.get('client');
  const emailFilter = searchParams.get('email');
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [downloadingPlanId, setDownloadingPlanId] = useState(null);
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'client_name'
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchPlans();
    fetchUsage();
  }, []);

  useEffect(() => {
    let filtered = plans;
    
    // Apply email filter if provided (preferred method)
    if (emailFilter) {
      filtered = filtered.filter(plan => 
        plan.client_email === emailFilter
      );
    }
    // Fallback to client name filter for backward compatibility
    else if (clientFilter) {
      filtered = filtered.filter(plan => 
        plan.client_name === clientFilter
      );
    }
    
    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(plan =>
        plan.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        plan.client_email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'client_name':
          return (a.client_name || '').localeCompare(b.client_name || '');
        case 'newest':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    setFilteredPlans(filtered);
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [searchQuery, plans, clientFilter, emailFilter, sortBy]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredPlans.length / PLANS_PER_PAGE);
  const startIndex = (currentPage - 1) * PLANS_PER_PAGE;
  const endIndex = startIndex + PLANS_PER_PAGE;
  const currentPlans = filteredPlans.slice(startIndex, endIndex);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/professional-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleViewPlan = (planId) => {
    navigate(`/advisor/plan/${planId}`);
  };

  const handleDownloadPDF = async (planId, clientName) => {
    try {
      setDownloadingPlanId(planId);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/advisor/plan/${planId}/download-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${clientName}_Financial_Plan_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF. Please try again.');
    } finally {
      setDownloadingPlanId(null);
    }
  };

  const formatCurrency = (amount) => {
    // Handle invalid or missing values
    if (amount === null || amount === undefined || isNaN(amount)) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div data-testid="plans-skeleton" className="max-w-7xl mx-auto animate-pulse">
          <div className="h-10 w-48 bg-gray-800 rounded-lg mb-2" />
          <div className="h-5 w-64 bg-gray-800/60 rounded mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-gray-800/40 rounded-xl" />)}
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-gray-800/40 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="my-plans-page" className="min-h-screen p-4 lg:p-8 relative overflow-hidden">
      <Navigation user={user} onLogout={onLogout} />
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-purple-600/[0.06] to-pink-500/[0.06] blur-3xl" animate={{ x: [0, -25, 0], y: [0, 30, 0] }} transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }} style={{ bottom: '10%', right: '5%' }} />
        </div>
      )}
      <motion.div className="max-w-7xl mx-auto relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        
        {/* Header */}
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-purple-500 to-pink-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
                <h1 data-testid="plans-header" className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
                  {(clientFilter || emailFilter) ? <>Plans for <span className="bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent">{filteredPlans[0]?.client_name || clientFilter || 'Client'}</span></> : <><span className="bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent">My</span> Plans</>}
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400 ml-5 text-sm">
                {(clientFilter || emailFilter)
                  ? `All financial plans created for this client`
                  : 'View and manage all created financial plans'
                }
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
              {(clientFilter || emailFilter) && (
                <Button 
                  onClick={() => navigate('/advisor/clients')}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Clients
                </Button>
              )}
              <Button 
                onClick={() => {
                  if (usage?.subscription_status === 'expired') {
                    navigate('/advisor/pricing');
                  } else {
                    // If viewing plans for a specific client, pre-select that client by email
                    if (emailFilter) {
                      navigate(`/advisor/create-plan?clientEmail=${encodeURIComponent(emailFilter)}`);
                    } else if (clientFilter) {
                      navigate(`/advisor/create-plan?client=${encodeURIComponent(clientFilter)}`);
                    } else {
                      navigate('/advisor/create-plan');
                    }
                  }
                }}
                disabled={usage?.subscription_status === 'expired'}
                className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                title={usage?.subscription_status === 'expired' ? 'Trial expired - Choose a plan to continue' : ''}
              >
                {usage?.subscription_status === 'expired' ? 'Trial Expired - Choose Plan' : 'Create New Plan'}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div data-testid="plans-stats" className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {[
            { label: 'Total Plans', value: filteredPlans.length, sub: 'All-time', icon: FileText, gradient: 'from-purple-500 to-pink-400', iconBg: 'bg-purple-500/10', iconColor: 'text-purple-400' },
            { label: (clientFilter || emailFilter) ? 'Client' : 'Unique Clients', value: (clientFilter || emailFilter) ? '1' : new Set(filteredPlans.map(p => p.client_email)).size, sub: (clientFilter || emailFilter) ? (filteredPlans[0]?.client_name || clientFilter || emailFilter) : 'All-time', icon: User, gradient: 'from-blue-500 to-cyan-400', iconBg: 'bg-blue-500/10', iconColor: 'text-blue-400' },
            { label: 'New Plans', value: filteredPlans.filter(p => { const d = new Date(p.created_at); const n = new Date(); return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); }).length, sub: 'This month', icon: Calendar, gradient: 'from-emerald-500 to-teal-400', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400' },
          ].map((stat, idx) => (
            <motion.div
              key={stat.label}
              data-testid={`plans-stat-${idx}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: isMobile ? 0 : 0.1 + idx * 0.07 }}
              whileHover={!isMobile ? { y: -4, transition: { duration: 0.25 } } : {}}
              className="group"
            >
              <Card className="p-5 border-gray-200 dark:border-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700/80 transition-all duration-300 hover:shadow-xl hover:shadow-black/20 relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-500`} />
                <div className="relative z-10 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium tracking-wide uppercase text-gray-500 mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-gray-100"><AnimatedNumber value={stat.value} /></p>
                    <p className="text-xs text-gray-500 mt-1">{stat.sub}</p>
                  </div>
                  <motion.div className={`w-10 h-10 rounded-lg ${stat.iconBg} flex items-center justify-center`} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2 + idx * 0.07, type: "spring", stiffness: 260 }}>
                    <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
                  </motion.div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Search and Sort Bar */}
        {!clientFilter && (
          <Card className="glass p-4 mb-6 dark:bg-gray-800">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search plans by client name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-auto dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600">
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    {sortBy === 'newest' ? 'Newest First' : sortBy === 'oldest' ? 'Oldest First' : 'Client Name'}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setSortBy('newest')}>
                    Newest First
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('oldest')}>
                    Oldest First
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('client_name')}>
                    Client Name
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Card>
        )}

        {/* Plans List */}
        {filteredPlans.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Card data-testid="plans-empty-state" className="p-12 border-gray-200 dark:border-gray-800/60 text-center">
              <motion.div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 flex items-center justify-center" animate={!isMobile ? { y: [0, -6, 0] } : {}} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
                <Sparkles className="w-7 h-7 text-purple-400/60" />
              </motion.div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {plans.length === 0 ? 'No plans yet' : 'No plans found'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                {plans.length === 0 
                  ? 'Create your first professional financial plan'
                  : clientFilter 
                    ? `No plans found for ${clientFilter}`
                    : 'Try adjusting your search query'}
              </p>
            <div className="flex gap-2 justify-center">
              {clientFilter && (
                <Button 
                  onClick={() => navigate('/advisor/clients')}
                  variant="outline"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Clients
                </Button>
              )}
              <Button 
                onClick={() => {
                  if (usage?.subscription_status === 'expired') {
                    navigate('/advisor/pricing');
                  } else {
                    // If viewing plans for a specific client, pre-select that client by email
                    if (emailFilter) {
                      navigate(`/advisor/create-plan?clientEmail=${encodeURIComponent(emailFilter)}`);
                    } else if (clientFilter) {
                      navigate(`/advisor/create-plan?client=${encodeURIComponent(clientFilter)}`);
                    } else {
                      navigate('/advisor/create-plan');
                    }
                  }
                }}
                disabled={usage?.subscription_status === 'expired'}
                className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                title={usage?.subscription_status === 'expired' ? 'Trial expired - Choose a plan to continue' : ''}
              >
                {usage?.subscription_status === 'expired' ? 'Trial Expired - Choose Plan' : 'Create Plan'}
              </Button>
            </div>
          </Card>
          </motion.div>
        ) : (
          <>
            <div className="space-y-4">
              {currentPlans.map((plan, index) => (
                <motion.div
                  key={plan.plan_id || index}
                  data-testid={`plan-card-${index}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: isMobile ? 0 : index * 0.05 }}
                  whileHover={!isMobile ? { y: -3, transition: { duration: 0.25 } } : {}}
                  className="group"
                >
                <Card className="p-5 border-gray-200 dark:border-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700/80 transition-all duration-300 hover:shadow-xl hover:shadow-black/20">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {plan.client_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">
                          {plan.client_name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{plan.client_email}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Age: </span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                              {plan.client_profile?.age || plan.age ? `${plan.client_profile?.age || plan.age} years` : 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Income: </span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                              {(plan.financial_snapshot?.monthly_income || plan.monthly_income) ? `${formatCurrency(plan.financial_snapshot?.monthly_income || plan.monthly_income)}/mo` : 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Goals: </span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                              {plan.goals?.length || 0}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Created: </span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                              {new Date(plan.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons - Right Aligned */}
                    <div className="flex gap-3 justify-end items-center border-t border-gray-200 dark:border-gray-700 pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPDF(plan.plan_id, plan.client_name)}
                        disabled={downloadingPlanId === plan.plan_id}
                        className="min-w-[130px]"
                      >
                        {downloadingPlanId === plan.plan_id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleViewPlan(plan.plan_id)}
                        className="bg-purple-600 hover:bg-purple-700 min-w-[120px]"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Plan
                      </Button>
                    </div>
                  </div>
                </Card>
                </motion.div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}

      </motion.div>
      <Footer />
    </div>
  );
}
